import {
  extractOrderPageForItem,
  saveOrderPagePdfForItem,
} from "./api/ProcurementClient";
import {
  ExtractedOrderPageResult,
  OrderPageFields,
  OrderPagePdfUpload,
} from "./models/OrderPageExtraction";
import { ProductPageSnapshot } from "./models/ProductPageExtraction";

export interface RfqPartNumberForPage {
  value: string;
  lineNumber: number;
  /** True for the line whose vendor quote is open. */
  openLine: boolean;
}

export interface ActiveVendorQuoteInfo {
  rfqId: number;
  itemId: number;
  /** Null while the vendor quote is unsaved. */
  quoteId: number | null;
  rfqNumber: string;
  poNumber: string;
  lineNumber: number;
  lineName: string;
  vendorName: string;
  current: OrderPageFields;
  rfqPartNumbers: RfqPartNumberForPage[];
}

export interface ActiveVendorQuoteHandle {
  getInfo: () => ActiveVendorQuoteInfo | null;
  /** Fills the editor (unsaved) and returns the labels of the fields that changed. */
  apply: (fields: OrderPageFields) => string[];
}

interface RegisteredEditor {
  handle: ActiveVendorQuoteHandle;
  activatedAt: number;
}

/** Open vendor quote editors; the most recently opened or clicked one is the target. */
const editors: RegisteredEditor[] = [];

export function registerActiveVendorQuote(
  handle: ActiveVendorQuoteHandle
): () => void {
  editors.push({ handle, activatedAt: Date.now() });
  return () => {
    const index = editors.findIndex((entry) => entry.handle === handle);
    if (index >= 0) editors.splice(index, 1);
  };
}

export function markActiveVendorQuote(handle: ActiveVendorQuoteHandle): void {
  const entry = editors.find((e) => e.handle === handle);
  if (entry) entry.activatedAt = Date.now();
}

function activeEditor(): { info: ActiveVendorQuoteInfo; entry: RegisteredEditor } | null {
  const sorted = [...editors].sort((a, b) => b.activatedAt - a.activatedAt);
  for (const entry of sorted) {
    const info = entry.handle.getInfo();
    if (info) return { info, entry };
  }
  return null;
}

function editorForItem(itemId: number): { info: ActiveVendorQuoteInfo; entry: RegisteredEditor } {
  const sorted = [...editors].sort((a, b) => b.activatedAt - a.activatedAt);
  for (const entry of sorted) {
    const info = entry.handle.getInfo();
    if (info?.itemId === itemId) return { info, entry };
  }
  throw new Error(
    "That vendor quote is no longer open in the procurement app. Open it and try again."
  );
}

type ActiveVendorQuoteBridge = () =>
  | (ActiveVendorQuoteInfo & { activatedAt: number })
  | null;
type PreviewOrderPage = (detail: {
  itemId: number;
  page: ProductPageSnapshot;
}) => Promise<ExtractedOrderPageResult>;
type ApplyOrderPage = (detail: {
  itemId: number;
  fields: OrderPageFields;
}) => { applied: string[] };
type SaveOrderPagePdf = (detail: {
  itemId: number;
  pdfBase64: string;
  url: string;
  vendorName: string;
}) => Promise<OrderPagePdfUpload>;

declare global {
  interface Window {
    __procurementActiveVendorQuote?: ActiveVendorQuoteBridge;
    __procurementPreviewOrderPage?: PreviewOrderPage;
    __procurementApplyOrderPage?: ApplyOrderPage;
    __procurementSaveOrderPagePdf?: SaveOrderPagePdf;
  }
}

/**
 * The extension's Order card calls these in the app tab (MAIN world), so reads and
 * uploads use the app's signed-in API session.
 */
export function registerOrderPageBridge(): () => void {
  const active: ActiveVendorQuoteBridge = () => {
    const found = activeEditor();
    return found ? { ...found.info, activatedAt: found.entry.activatedAt } : null;
  };
  const preview: PreviewOrderPage = async ({ itemId, page }) => {
    const { info } = editorForItem(itemId);
    return extractOrderPageForItem(info.rfqId, itemId, page);
  };
  const apply: ApplyOrderPage = ({ itemId, fields }) => {
    const { entry } = editorForItem(itemId);
    return { applied: entry.handle.apply(fields ?? {}) };
  };
  const savePdf: SaveOrderPagePdf = async ({ itemId, pdfBase64, url, vendorName }) => {
    const { info } = editorForItem(itemId);
    return saveOrderPagePdfForItem(info.rfqId, itemId, {
      pdfBase64,
      url,
      vendorName: vendorName || info.vendorName,
    });
  };

  window.__procurementActiveVendorQuote = active;
  window.__procurementPreviewOrderPage = preview;
  window.__procurementApplyOrderPage = apply;
  window.__procurementSaveOrderPagePdf = savePdf;
  return () => {
    if (window.__procurementActiveVendorQuote === active) {
      delete window.__procurementActiveVendorQuote;
    }
    if (window.__procurementPreviewOrderPage === preview) {
      delete window.__procurementPreviewOrderPage;
    }
    if (window.__procurementApplyOrderPage === apply) {
      delete window.__procurementApplyOrderPage;
    }
    if (window.__procurementSaveOrderPagePdf === savePdf) {
      delete window.__procurementSaveOrderPagePdf;
    }
  };
}
